/**
 * prepBookingMerge.ts
 * Pure model for folding a source's rock-prep bookings into ONE bar per machine
 * (BUG-2026-0193, 2026-09-04).
 *
 * The scheduler inserts prep (Cutting / Grinding / Conformity) PER TEST LINE on
 * purpose — opt-outs, sticky removals and occupancy all hang off individual
 * ScheduleTask rows (scheduleTaskPrepInserter.ts) — and each step is a flat
 * 15–25 min block minute-packed onto the same first free day. So a work order
 * with four rock tests books four "Cutting" bars on the saw with an IDENTICAL
 * span, and the Machine Schedule grid drew them on top of each other. The lab
 * reads prep as one activity ("this should be one bar with all the effort
 * combined"), the same way the Phase-1 task table already folds the rows
 * (prepRollup.ts, BUG-2026-0165). This module is the grid-side fold:
 * DISPLAY-only, data-only, no JSX, unit-tested.
 *
 * Merge key = machine · source (work order, else quote) · scenario · status ·
 * other-plan flag. Within a key, bookings form CLUSTERS of contiguous working
 * days (a weekend / holiday gap between them does not split a cluster; a free
 * working day does) so a batch re-synced weeks later stays its own bar.
 * Single-member clusters are left as plain bookings.
 *
 * The merged bar is a synthetic MachineBookingCalendar: it borrows the PRIMARY
 * member's identity (earliest start, then key) so selection / conflict badges /
 * `data-booking-key` keep working, spans min(start)..max(end), and carries the
 * members in `mergedPrepBookings`. `expandPrepPatch` turns a drag on the merged
 * bar into one patch per member (same day delta, same target machine).
 */

import type { MachineBookingCalendar, BookingAssignee } from '../types/schedule';
import type { BookingPlacementPatch } from '../hooks/useScheduleData';

// ---------------------------------------------------------------------------
// Date helpers (pure, UTC)
// ---------------------------------------------------------------------------

export type WorkingDayPredicate = (iso: string) => boolean;

/** Add N calendar days to a 'YYYY-MM-DD' string. */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Whole calendar days from `a` to `b` ('YYYY-MM-DD'); negative when b < a. */
export function daysBetweenIso(a: string, b: string): number {
  const ms = Date.parse(`${b.slice(0, 10)}T00:00:00Z`) - Date.parse(`${a.slice(0, 10)}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/** Default working-day rule when no holiday feed is available: Mon–Fri. */
export const weekdayOnly: WorkingDayPredicate = (iso) => {
  const dow = new Date(`${iso.slice(0, 10)}T00:00:00Z`).getUTCDay();
  return dow !== 0 && dow !== 6;
};

/**
 * True when `nextStart` follows `prevEnd` with NO working day strictly between
 * them — same day, overlap, the next day, or only weekend/holiday days in the
 * gap. A free working day in between breaks contiguity.
 */
export function contiguousAcrossNonWorking(
  prevEnd: string,
  nextStart: string,
  isWorkingDay: WorkingDayPredicate = weekdayOnly,
): boolean {
  const gap = daysBetweenIso(prevEnd, nextStart);
  if (gap <= 1) return true;
  for (let i = 1; i < gap; i++) {
    if (isWorkingDay(addDaysIso(prevEnd, i))) return false;
  }
  return true;
}

/**
 * Group date-spanned items into clusters of contiguous working days (see
 * contiguousAcrossNonWorking). Items are sorted by start (then end) first; each
 * cluster's reach grows to its latest end so an early long item absorbs later
 * short ones. Pure — returns new arrays, input untouched.
 */
export function clusterContiguous<T>(
  items: readonly T[],
  getStart: (t: T) => string,
  getEnd: (t: T) => string,
  isWorkingDay: WorkingDayPredicate = weekdayOnly,
): T[][] {
  const sorted = [...items].sort((a, b) => {
    const s = getStart(a).localeCompare(getStart(b));
    return s !== 0 ? s : getEnd(a).localeCompare(getEnd(b));
  });
  const clusters: T[][] = [];
  let current: T[] = [];
  let reach = '';
  for (const item of sorted) {
    if (current.length > 0 && contiguousAcrossNonWorking(reach, getStart(item), isWorkingDay)) {
      current.push(item);
    } else {
      if (current.length > 0) clusters.push(current);
      current = [item];
      reach = getEnd(item);
    }
    if (getEnd(item) > reach) reach = getEnd(item);
  }
  if (current.length > 0) clusters.push(current);
  return clusters;
}

// ---------------------------------------------------------------------------
// Prep identification + labels
// ---------------------------------------------------------------------------

/** A merged prep bar — the synthetic row carrying its members. */
export type MergedPrepBooking = MachineBookingCalendar & { mergedPrepBookings: MachineBookingCalendar[] };

export function isMergedPrepBooking(b: MachineBookingCalendar): b is MergedPrepBooking {
  return Array.isArray(b.mergedPrepBookings) && b.mergedPrepBookings.length > 1;
}

/** True for a booking whose linked task is an auto-inserted prep step. */
export function isPrepBooking(b: MachineBookingCalendar): boolean {
  return b.TaskIsPrepStep === true && !b.EventType && !!b.ScheduleTaskKey;
}

/**
 * The merge key — machine · source (work order, else quote) · scenario ·
 * status · other-plan flag. Null when the booking is not prep or has no source
 * (a manual prep-typed task never folds).
 */
export function prepMergeGroupKey(b: MachineBookingCalendar): string | null {
  if (!isPrepBooking(b)) return null;
  const source = b.TaskWorkOrderKey ?? b.TaskQuoteKey ?? b.QuoteKey ?? null;
  if (!source) return null;
  return [b.MachineKey, source, b.ScenarioKey, b.Status, b.isOtherScenario ? 'other' : 'own'].join('|');
}

/** "Cutting (Preparation)" → "Cutting"; unknown → "Prep". */
export function prepStepShortName(taskName: string | null | undefined): string {
  const raw = (taskName ?? '').trim();
  if (!raw) return 'Prep';
  return raw.replace(/\s*\((?:preparation|prep)\)\s*$/i, '').trim() || raw;
}

/**
 * Step breakdown for a set of prep members: "Cutting × 4" or
 * "Cutting × 2, Grinding × 1" (first-appearance order).
 */
export function prepStepBreakdown(taskNames: ReadonlyArray<string | null | undefined>): string {
  const counts = new Map<string, number>();
  for (const n of taskNames) {
    const short = prepStepShortName(n);
    counts.set(short, (counts.get(short) ?? 0) + 1);
  }
  return [...counts.entries()].map(([name, n]) => `${name} × ${n}`).join(', ');
}

/** Σ member minutes, or null when no member carries minutes. */
export function prepTotalMinutes(minutes: ReadonlyArray<number | null | undefined>): number | null {
  let total: number | null = null;
  for (const m of minutes) {
    if (typeof m === 'number' && Number.isFinite(m)) total = (total ?? 0) + m;
  }
  return total;
}

/** "60 min" / "2 h 15 min" — compact machine-time text. */
export function formatMinutesCompact(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

/** The merged bar's TaskName: "Preparation — Cutting × 4". */
export function prepMergeTaskName(members: readonly MachineBookingCalendar[]): string {
  return `Preparation — ${prepStepBreakdown(members.map((m) => m.TaskName))}`;
}

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

/** Earliest start, then key — the member whose identity the merged bar borrows. */
function pickPrimary(members: readonly MachineBookingCalendar[]): MachineBookingCalendar {
  return [...members].sort(
    (a, b) => a.StartDate.localeCompare(b.StartDate) || a.MachineBookingKey.localeCompare(b.MachineBookingKey),
  )[0];
}

function unionAssignees(members: readonly MachineBookingCalendar[]): BookingAssignee[] {
  const seen = new Map<string, BookingAssignee>();
  for (const m of members) for (const a of m.Assignees ?? []) if (!seen.has(a.UserKey)) seen.set(a.UserKey, a);
  return [...seen.values()];
}

/** Build the synthetic merged row for ≥ 2 members (sorted by start, then key). */
export function buildMergedPrepBooking(members: readonly MachineBookingCalendar[]): MergedPrepBooking {
  const sorted = [...members].sort(
    (a, b) => a.StartDate.localeCompare(b.StartDate) || a.MachineBookingKey.localeCompare(b.MachineBookingKey),
  );
  const primary = pickPrimary(sorted);
  let start = primary.StartDate;
  let end = primary.EndDate;
  for (const m of sorted) {
    if (m.StartDate < start) start = m.StartDate;
    if (m.EndDate > end) end = m.EndDate;
  }
  return {
    ...primary,
    StartDate: start,
    EndDate: end,
    TaskName: prepMergeTaskName(sorted),
    TaskMachineMinutes: prepTotalMinutes(sorted.map((m) => m.TaskMachineMinutes)),
    Assignees: unionAssignees(sorted),
    // A merged bar is a whole-day block, never a split share.
    SplitQty: null,
    mergedPrepBookings: sorted,
  };
}

/**
 * Fold a booking list for display: non-prep rows pass through untouched (order
 * preserved); each prep merge-key's contiguous clusters collapse into ONE
 * synthetic bar at the position of their first member. Clusters of one stay
 * the plain original booking. Idempotent on already-merged input.
 */
export function mergePrepBookings(
  bookings: readonly MachineBookingCalendar[],
  isWorkingDay: WorkingDayPredicate = weekdayOnly,
): MachineBookingCalendar[] {
  const groups = new Map<string, MachineBookingCalendar[]>();
  for (const b of bookings) {
    if (isMergedPrepBooking(b)) continue; // idempotence: re-fold from members below
    const key = prepMergeGroupKey(b);
    if (!key) continue;
    const list = groups.get(key) ?? [];
    list.push(b);
    groups.set(key, list);
  }
  // Already-merged rows contribute their members (so a second pass is stable).
  for (const b of bookings) {
    if (!isMergedPrepBooking(b)) continue;
    for (const m of b.mergedPrepBookings) {
      const key = prepMergeGroupKey(m);
      if (!key) continue;
      const list = groups.get(key) ?? [];
      list.push(m);
      groups.set(key, list);
    }
  }

  // memberKey → the merged row that replaces it (first member renders it).
  const replacement = new Map<string, MachineBookingCalendar>();
  const swallowed = new Set<string>();
  for (const members of groups.values()) {
    for (const cluster of clusterContiguous(members, (m) => m.StartDate, (m) => m.EndDate, isWorkingDay)) {
      if (cluster.length < 2) continue;
      const merged = buildMergedPrepBooking(cluster);
      let first = true;
      for (const m of cluster) {
        if (first) replacement.set(m.MachineBookingKey, merged);
        else swallowed.add(m.MachineBookingKey);
        first = false;
      }
    }
  }

  const out: MachineBookingCalendar[] = [];
  const emitted = new Set<string>();
  const visit = (b: MachineBookingCalendar) => {
    if (swallowed.has(b.MachineBookingKey)) return;
    const merged = replacement.get(b.MachineBookingKey);
    if (merged) {
      if (!emitted.has(merged.MachineBookingKey)) { out.push(merged); emitted.add(merged.MachineBookingKey); }
      return;
    }
    out.push(b);
  };
  for (const b of bookings) {
    if (isMergedPrepBooking(b)) b.mergedPrepBookings.forEach(visit);
    else visit(b);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Drag fan-out
// ---------------------------------------------------------------------------

export interface PrepPatchPart {
  booking: MachineBookingCalendar;
  patch: BookingPlacementPatch;
}

/**
 * Turn a placement patch aimed at a (possibly merged) bar into one patch per
 * real booking. A plain booking yields itself unchanged. For a merged bar:
 *   - a MOVE (StartDate and/or EndDate) shifts every member by the same whole-
 *     day delta the merged bar was dragged, keeping each member's own span;
 *   - a machine change applies to every member.
 * The primary member comes first (it is the one gated for conflicts).
 */
export function expandPrepPatch(
  booking: MachineBookingCalendar,
  patch: BookingPlacementPatch,
): PrepPatchPart[] {
  if (!isMergedPrepBooking(booking)) return [{ booking, patch }];
  const delta = patch.StartDate !== undefined
    ? daysBetweenIso(booking.StartDate, patch.StartDate)
    : patch.EndDate !== undefined
      ? daysBetweenIso(booking.EndDate, patch.EndDate)
      : 0;
  const hasDates = patch.StartDate !== undefined || patch.EndDate !== undefined;
  const primaryKey = booking.MachineBookingKey;
  const ordered = [...booking.mergedPrepBookings].sort((a, b) =>
    (a.MachineBookingKey === primaryKey ? -1 : b.MachineBookingKey === primaryKey ? 1 : 0),
  );
  return ordered.map((member) => {
    const p: BookingPlacementPatch = {};
    if (hasDates) {
      p.StartDate = addDaysIso(member.StartDate, delta);
      p.EndDate = addDaysIso(member.EndDate, delta);
    }
    if (patch.MachineKey !== undefined) p.MachineKey = patch.MachineKey;
    return { booking: member, patch: p };
  });
}
